import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, ArrowUp } from 'lucide-react';
import { openEstimate } from './EstimatePopup';

/**
 * FloatingActions — bottom-right stacked buttons that fade in after scroll.
 *  - GET ESTIMATE pill (always visible once user scrolls past 600px)
 *  - Back-to-top circular button (appears once user scrolls past 600px)
 *
 * Hidden in the hero region so they don't compete with the hero CTAs.
 */
export default function FloatingActions() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const backToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 30 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed right-4 md:right-6 bottom-4 md:bottom-6 z-40 flex flex-col items-end gap-3 print:hidden"
        >
          <button
            onClick={openEstimate}
            data-cursor="hover"
            data-cursor-label="Estimate"
            data-testid="floating-get-estimate"
            className="group inline-flex items-center gap-3 bg-[#6A8F3E] hover:bg-[#7BA047] text-[#1A1A1A] pl-5 pr-3 py-3 rounded-full shadow-[0_14px_40px_-12px_rgba(106,143,62,0.55)] transition-all"
          >
            <Calculator size={15} strokeWidth={2.6} />
            <span className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase font-extrabold">Get Estimate</span>
            <span className="w-7 h-7 rounded-full bg-[#162A1E] flex items-center justify-center group-hover:bg-[#0F1A14] transition-colors">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#6A8F3E" strokeWidth="3" strokeLinecap="round" className="group-hover:translate-x-0.5 transition-transform">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </span>
          </button>

          <button
            onClick={backToTop}
            aria-label="Back to top"
            data-cursor="hover"
            data-testid="floating-back-to-top"
            className="w-11 h-11 rounded-full bg-[#314F2B]/80 backdrop-blur border border-[#6A8F3E]/35 hover:bg-[#6A8F3E] hover:text-[#314F2B] text-[#6A8F3E] flex items-center justify-center transition-all"
          >
            <ArrowUp size={16} strokeWidth={2.2} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
